import { useEffect, useRef, useState } from "react";
import { IPhoneCustomer, PHONE_MIN_SEARCH_LENGTH } from "../models/IPhoneOrder";
import { phoneErrorMessage, searchPhoneCustomers } from "../services/phoneOrderService";

// Tek arama kutusu, iki soru: sunucu sorgunun numara mi ad mi oldugunu kendisi ayirir.
const SEARCH_ERROR: Record<string, string> = {
  SearchQueryTooShort: "Daha fazla harf yazın.",
  InvalidPhoneSearchPrefix: "Bu bir telefon numarasına benzemiyor. Numaranın ilk 3-10 hanesini yazın.",
  RestourantAccessDenied: "Bu restoranın müşteri dizinini görme yetkiniz yok.",
};

interface Props {
  restaurantId: string;
  selectedId?: string | null;
  onSelect: (customer: IPhoneCustomer) => void;
  /** Sonuc yoksa gosterilecek eylem (ornegin "Yeni müşteri kaydet"). */
  onCreateNew?: (query: string) => void;
  autoFocus?: boolean;
}

export default function PhoneCustomerSearchBox({
  restaurantId, selectedId, onSelect, onCreateNew, autoFocus,
}: Props) {
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<IPhoneCustomer[] | null>(null);
  const [limitReached, setLimitReached] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const reqId = useRef(0);

  const trimmed = query.trim();
  const tooShort = trimmed.length > 0 && trimmed.length < PHONE_MIN_SEARCH_LENGTH;

  useEffect(() => {
    if (trimmed.length < PHONE_MIN_SEARCH_LENGTH) {
      setItems(null);
      setError("");
      setLoading(false);
      return;
    }
    const mine = ++reqId.current;
    setLoading(true);
    const timer = setTimeout(() => {
      searchPhoneCustomers(restaurantId, trimmed)
        .then((res) => {
          if (mine !== reqId.current) return;
          setItems(res.data?.items ?? []);
          setLimitReached(!!res.data?.isLimitReached);
          setError("");
        })
        .catch((err) => {
          if (mine !== reqId.current) return;
          setItems(null);
          setError(phoneErrorMessage(err, SEARCH_ERROR, "Müşteri aranamadı."));
        })
        .finally(() => { if (mine === reqId.current) setLoading(false); });
    }, 350);
    return () => clearTimeout(timer);
  }, [restaurantId, trimmed]);

  return (
    <div className="pc-search">
      <div className="dinein-search" style={{ marginBottom: 8 }}>
        <i className="bi bi-search"></i>
        <input
          autoFocus={autoFocus}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Telefon ya da ad soyad ile ara..."
        />
        {loading
          ? <span className="spinner-border spinner-border-sm" style={{ marginRight: 10 }} />
          : query && <button onClick={() => setQuery("")}><i className="bi bi-x-lg"></i></button>}
      </div>

      <div className="dinein-hint" style={{ marginBottom: 8 }}>
        <i className="bi bi-info-circle me-1"></i>
        0533, 533, +90533 aynı sonucu verir. Numaranın başından itibaren aranır.
      </div>

      {tooShort && (
        <div className="dinein-small dinein-muted">En az {PHONE_MIN_SEARCH_LENGTH} karakter yazın.</div>
      )}

      {error && (
        <div className="dinein-alert dinein-alert--error"><i className="bi bi-exclamation-circle-fill"></i><span>{error}</span></div>
      )}

      {items && items.length === 0 && (
        <div className="pc-empty">
          <i className="bi bi-person-x"></i>
          <div>Dizinde "{trimmed}" ile eşleşen müşteri yok.</div>
          {onCreateNew && (
            <button className="dinein-btn dinein-btn--primary" style={{ marginTop: 10 }} onClick={() => onCreateNew(trimmed)}>
              <i className="bi bi-person-plus-fill me-1"></i>Yeni müşteri bilgisi gir
            </button>
          )}
        </div>
      )}

      {items && items.length > 0 && (
        <>
          <div className="pc-list">
            {items.map((c) => (
              <button key={c.id} type="button"
                className={`pc-row${selectedId === c.id ? " pc-row--active" : ""}`}
                onClick={() => onSelect(c)}>
                <div className="pc-row__main">
                  <div className="pc-row__name">
                    {c.firstName} {c.lastName}
                    {selectedId === c.id && <i className="bi bi-check-circle-fill ms-2"></i>}
                  </div>
                  <div className="pc-row__phone"><i className="bi bi-telephone me-1"></i>{c.phoneNumber}</div>
                  <div className="pc-row__addr">
                    <i className="bi bi-geo-alt me-1"></i>
                    {c.cityName}, {c.townName}, {c.streetName}
                  </div>
                </div>
                <i className="bi bi-chevron-right pc-row__chevron"></i>
              </button>
            ))}
          </div>
          {limitReached && (
            <div className="dinein-alert dinein-alert--warn">
              <i className="bi bi-exclamation-triangle-fill"></i>
              <span>İlk {items.length} kayıt gösteriliyor, daha fazlası olabilir. Aramayı daraltmak için biraz daha yazın.</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
